import { CompanyDto } from 'utils/wemble-api.generated';

type OrganizationSettings = {
  name: string;
  disableProjects?: boolean;
  disableProjectManager?: boolean;
  enableProjectStages?: boolean;
  disableAbsencePlanner?: boolean;
  absencePlannerviewOnly?: boolean;
  projectInvoicing?: boolean;
  disableCompetences?: boolean;
  /** Who manages competencies: creating, renaming, moving and deleting
   *  categories and competencies, and adding or removing people on them.
   *  Default (unset): admins only (admin / companyAdmin / administratorPriviliges);
   *  people can still add themselves and set their own levels. `true` keeps the
   *  legacy behaviour where everyone can. Enforced in the app, not the API.
   *  Candidate for the admin dashboard. */
  competencesEditableByEveryone?: boolean;
  /** People can't set their own levels; only admins set levels (Crowell). */
  disableCompetencesEditSelf?: boolean;
  disableMessage?: boolean;
  allOfficesDefault?: boolean;
  allGroupsDefault?: boolean;
  projectsAllGroups?: boolean;
  enableUtilizationView?: boolean;
  hideUpdateRemindersSetting?: boolean;
  enableDetailedViewHours?: boolean;
  enableDetailedViewComments?: boolean;
  enableMatterPM?: boolean;
  expandComment?: boolean; // TODO: All firms have this enabled — remove flag and make it the default behavior
  enableCustomHtml?: boolean;
  /** Show the Search input and Sort dropdown on mobile (they're hidden by default
   *  on small viewports). Crowell asked for them back. */
  mobileSearchAndSort?: boolean;
  newEntraUI?: boolean;
  ssoEnabled?: boolean;

  disableWFH?: boolean;
  elseWhereOption?: boolean;
  detailedViewAdminDefault?: boolean;
  /** Keep the normal home page for people with `detailedViewPriviliges`, instead of the
   *  forecast + matter comments layout built for Hannes Snellman (Selmer, 2026-10-05). */
  disableMatterLayout?: boolean;

  /** Controls who can see experience search, profiles, and filters.
   *  undefined = off, 'all' = everyone, 'admin' = admins only, string[] = specific emails */
  experienceView?: 'all' | 'admin' | string[];
};

export default function (company: CompanyDto | string | null | undefined): OrganizationSettings {
  const companyId = typeof company === 'string' ? company : company?._id;
  switch (companyId) {
    case '5bf56c679d6f50bd0e67d6c0':
      return {
        name: 'Mannheimer Swartling',
        expandComment: true,
        newEntraUI: true,
      };

    case '5da0c1370b30f993a68d898f':
      return {
        name: 'Hannes Snellman',
        enableUtilizationView: true,
        enableMatterPM: true,
        expandComment: true,
        enableDetailedViewHours: true,
        enableDetailedViewComments: true,
        disableProjects: false,
        enableProjectStages: true,
        disableCompetences: true,
        elseWhereOption: true,
        ssoEnabled: true,
        experienceView: ['maria.antrea@hannessnellman.com', 'taija.niku@hannessnellman.com'],
      };
    case '5e4d42f50fc8ec2139341a39':
      return {
        name: 'Selmer',
        disableProjects: true,
        disableMatterLayout: true,
        expandComment: true,
        disableWFH: true,
        ssoEnabled: true,
        // Live since 2026-10-02 (Hugo, 2026-10-04/05): experience search only for the people listed here:
        // Christoffer Haugen, and since 2026-10-05 Martin Ege and Camilla Bjørgo (Christoffer asked). The same
        // three also have detailedViewPriviliges, detailedViewHoursPriviliges and utilizationViewOthersPriviliges
        // on their users (other people's matters, hours and utilization); a stopgap until access follows admin
        // status / roles. Everyone else sees their own hours on their own profile.
        // Next, once Christoffer agrees: detailedViewAdminDefault: true and experienceView: 'admin' (admins see all).
        enableUtilizationView: true,
        enableDetailedViewHours: true,
        experienceView: ['c.haugen@selmer.no', 'm.ege@selmer.no', 'c.bjorgo@selmer.no'],
      };

    case '5e5cd5ec4a51560674ab9188':
      //SW GBG
      return {
        name: 'Setterwalls',
        competencesEditableByEveryone: true,
        expandComment: true,
        ssoEnabled: true
      };

    case '6011c36f110b63a4a08b6aab':
      return {
        name: 'Cirio',
        competencesEditableByEveryone: true,
        expandComment: true,
        disableProjects: true,
        disableWFH: true,


      };

    case '6239ed6a8398122b078e2019':
      return {
        name: 'Élan Zaak',
        disableProjectManager: true,
        enableProjectStages: true,
        projectInvoicing: true,
        expandComment: true,
      };
    case '62947730ce5f37dea9c5db79':
      return {
        name: 'Kvale',
        competencesEditableByEveryone: true,
        disableProjects: true,
        allOfficesDefault: true,
        expandComment: true
      };
    case '63491b42fef920f504b85c93':
      return {
        name: 'MAQS',
        competencesEditableByEveryone: true,
        disableProjects: true,
        disableMessage: true,
        allOfficesDefault: true,
        expandComment: true,
        elseWhereOption: true,
      };
    case '64ff3e804ec954850f158026':
      return {
        name: 'Crowell & Moring',
        allOfficesDefault: true,
        allGroupsDefault: true,
        projectsAllGroups: true,
        hideUpdateRemindersSetting: true,
        disableCompetencesEditSelf: true,
        enableCustomHtml: true,
        expandComment: true,
        disableWFH: true,
        mobileSearchAndSort: true,
        newEntraUI: true,
      };
    case '650197f48213ce0e6b0e5045':
      return {
        name: 'Synch',
        allOfficesDefault: false,
        allGroupsDefault: false,
        projectsAllGroups: true,
        absencePlannerviewOnly: false,
        enableProjectStages: true,
        enableUtilizationView: true,
        enableDetailedViewHours: true,
        expandComment: true,
        ssoEnabled: true,
        detailedViewAdminDefault: true,
        experienceView: ['erica.antonovic@synch.law'],

      };
    case '65f779d34b718376e412163f':
      return {
        name: 'Brækhus',
        competencesEditableByEveryone: true,
        expandComment: true,
        disableProjects: true,
        elseWhereOption: true
      };
    case '667d4e911ec312156ecad104':
      return {
        name: 'Hannes Snellman (Sweden)',
        expandComment: true,
        disableProjects: true,
      };
    case '67044f6c137d9032a3401dc5':
      return {
        name: 'TIQ',
        expandComment: true,
        disableProjects: true,
        disableWFH: true
      };
    case '68e638e0b75c9d41b3ccf22f':
      return {
        name: 'Ström',
        disableProjects: true,
        disableWFH: true,
        expandComment: true,
        enableDetailedViewHours: true,
        enableUtilizationView: true,
        detailedViewAdminDefault: true,
        experienceView: 'admin',
      };
    case '696f90ece833833822659197':
      return {
        name: 'Next Law',
        disableProjects: true,
        disableWFH: true,
        expandComment: true,

      };
    case '699379c260c2ae246a9d9282':
      return {
        name: 'BORN',
        disableProjects: true,
        disableWFH: true,
        expandComment: true,
        enableDetailedViewHours: true,
        enableUtilizationView: true,
        detailedViewAdminDefault: true,
        experienceView: 'all',
      };
    case '69df98004da4ce2d1801108e':
      return {
        name: 'Bokwall Rislund',
        disableProjects: true,
        disableWFH: true,
        expandComment: true,
        enableUtilizationView: true,
        enableDetailedViewHours: true,
        enableDetailedViewComments: true,
        detailedViewAdminDefault: true,
        experienceView: 'admin',
      };
    case '6a0c59f96afe3cb7aebfddae':
      return {
        name: 'Morris Law',
        disableProjects: true,
        allOfficesDefault: true, // Göteborg + Stockholm in one group: show the whole group across offices by default
        disableWFH: true,
        expandComment: true,
      };
    default:
      // Demo sandboxes (server/seeds/sandboxes.json) have no case of their own and use this
      // default, so keep it showing everything a demo should.
      return {
        name: 'Wemble',
        disableProjects: true,
        enableUtilizationView: true,
        enableDetailedViewHours: true,
        expandComment: true,
        detailedViewAdminDefault: true,
        disableWFH: true,
        newEntraUI: true,
        experienceView: 'all',
      };
  }
}

/** Check if a user can see experience features (profile, search filters, annotations) */
export function canViewExperience(org: OrganizationSettings, userEmail?: string | null, isAdmin?: boolean): boolean {
  if (!org.experienceView) return false;
  if (org.experienceView === 'all') return true;
  if (org.experienceView === 'admin') return !!isAdmin;
  return !!userEmail && org.experienceView.includes(userEmail.toLowerCase());
}

export type DomainSsoConfig = {
  companyId: string;
  ssoMethod: 'entra';
  installation?: string;
  forceSso?: boolean;
};

const domainConfigs: Record<string, DomainSsoConfig> = {
  '@crowell': { companyId: '64ff3e804ec954850f158026', ssoMethod: 'entra', installation: 'crowell.wemble.app', forceSso: true },
  '@faberlawgroup': { companyId: '64ff3e804ec954850f158026', ssoMethod: 'entra', installation: 'crowell.wemble.app', forceSso: true },
  '@mannheimerswartling': { companyId: '5bf56c679d6f50bd0e67d6c0', ssoMethod: 'entra' },
  '@msa': { companyId: '5bf56c679d6f50bd0e67d6c0', ssoMethod: 'entra' },
  '@hannessnellman': { companyId: '5da0c1370b30f993a68d898f', ssoMethod: 'entra' },
  '@selmer': { companyId: '5e4d42f50fc8ec2139341a39', ssoMethod: 'entra' },
  '@nextlaw': { companyId: '696f90ece833833822659197', ssoMethod: 'entra', forceSso: true },
  '@getwemble': { companyId: '657662a326e3fd00142a815f', ssoMethod: 'entra' },
  '@born.se': { companyId: '699379c260c2ae246a9d9282', ssoMethod: 'entra' },
  '@maqs': { companyId: '63491b42fef920f504b85c93', ssoMethod: 'entra' },
  '@braekhus': { companyId: '65f779d34b718376e412163f', ssoMethod: 'entra' },
  '@cirio': { companyId: '6011c36f110b63a4a08b6aab', ssoMethod: 'entra' },
  '@kvale': { companyId: '62947730ce5f37dea9c5db79', ssoMethod: 'entra' },
  '@setterwalls': { companyId: '5e5cd5ec4a51560674ab9188', ssoMethod: 'entra' },
  '@stroem': { companyId: '68e638e0b75c9d41b3ccf22f', ssoMethod: 'entra' },
  '@synch.law': { companyId: '650197f48213ce0e6b0e5045', ssoMethod: 'entra' },
  '@br-a.se': { companyId: '69df98004da4ce2d1801108e', ssoMethod: 'entra' },
  '@morrislaw.se': { companyId: '6a0c59f96afe3cb7aebfddae', ssoMethod: 'entra' },
};

export function getDomainSsoConfig(email: string): DomainSsoConfig | null {
  const lowerEmail = email.toLowerCase();
  for (const [domain, config] of Object.entries(domainConfigs)) {
    if (lowerEmail.includes(domain)) {
      return config;
    }
  }
  return null;
}

export function getCompanyIdByEmailDomain(email: string): string | null {
  return getDomainSsoConfig(email)?.companyId ?? null;
}
